import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  MenuItem,
  Snackbar,
  Alert
} from '@mui/material';

import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LayoutDashboard from '../layout/LayoutDashboard';
import '../CSS/ListeDeclarations.css';
import apiUrl from '../config';

const DECLARATIONS_LIST_CACHE_KEY = "liste_declarations_cdi_cache_v1";

const getCachedSalaries = () => {
  try {
    const cached = sessionStorage.getItem(DECLARATIONS_LIST_CACHE_KEY);
    return cached ? JSON.parse(cached) : [];
  } catch {
    sessionStorage.removeItem(DECLARATIONS_LIST_CACHE_KEY);
    return [];
  }
};

const saveSalariesCache = (data) => {
  sessionStorage.setItem(DECLARATIONS_LIST_CACHE_KEY, JSON.stringify(data));
};

const normalizeText = (value) => {
  if (!value) return "";

  return value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
};

const isCompteValide = (salarie) => {
  return salarie?.compte_status === "Compte validé";
};

const ListeDeclarations = () => {

  const [salaries, setSalaries] = useState(() => getCachedSalaries());
  const [loading, setLoading] = useState(() => getCachedSalaries().length === 0);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('Tous');
  const [filterDeclaration, setFilterDeclaration] = useState('Tous');

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const navigate = useNavigate();

  const fetchSalaries = async (silent = false) => {
    if (!silent && salaries.length === 0) {
      setLoading(true);
    }

    try {
      const token = localStorage.getItem('token');

      const response = await axios.get(
        `${apiUrl}/cdi-users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSalaries(response.data);
      saveSalariesCache(response.data);

    } catch (error) {
      console.error(error);

      setNotification({
        open: true,
        message: "Erreur lors du chargement des salariés.",
        severity: "error"
      });

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalaries(true);
  }, []);

  const filteredSalaries = useMemo(() => {
    let data = [...salaries];

    if (searchTerm.trim() !== '') {
      const term = normalizeText(searchTerm);

      data = data.filter((salarie) => {
        const content = normalizeText(`
          ${salarie.nom || ''}
          ${salarie.prenom || ''}
          ${salarie.email || ''}
          ${salarie.compte_status || ''}
          ${salarie.declarationsCount || 0}
        `);

        return content.includes(term);
      });
    }

    if (filterStatus === "valides") {
      data = data.filter((salarie) => isCompteValide(salarie));
    }

    if (filterStatus === "non_valides") {
      data = data.filter((salarie) => !isCompteValide(salarie));
    }

    if (filterDeclaration === "avec") {
      data = data.filter((salarie) => Number(salarie.declarationsCount || 0) > 0);
    }

    if (filterDeclaration === "sans") {
      data = data.filter((salarie) => Number(salarie.declarationsCount || 0) === 0);
    }

    return data;
  }, [salaries, searchTerm, filterStatus, filterDeclaration]);

  const totalSalaries = salaries.length;
  const totalDeclarations = salaries.reduce(
    (sum, salarie) => sum + Number(salarie.declarationsCount || 0),
    0
  );
  const totalComptesValides = salaries.filter((salarie) => isCompteValide(salarie)).length;
  const totalSansDeclaration = salaries.filter(
    (salarie) => Number(salarie.declarationsCount || 0) === 0
  ).length;

  const handleNavigateToDeclarations = (salarie) => {
    localStorage.setItem("selectedUserId", salarie.id);
    localStorage.setItem("selectedUserName", `${salarie.nom || ''} ${salarie.prenom || ''}`.trim());

    navigate("/Déclarations");
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterStatus('Tous');
    setFilterDeclaration('Tous');
  };

  const refreshData = async () => {
    sessionStorage.removeItem(DECLARATIONS_LIST_CACHE_KEY);

    setNotification({
      open: true,
      message: "Actualisation en cours...",
      severity: "info"
    });

    await fetchSalaries(false);
  };

  return (
    <LayoutDashboard>

      <div className="liste-declarations-page">

        <section className="liste-declarations-hero">
          <div>
            <span className="liste-declarations-badge">Déclarations</span>

            <h1>Liste des déclarations</h1>

            <p>
              Suivez rapidement les salariés CDI, le nombre de déclarations déposées et l’état des comptes.
            </p>
          </div>
        </section>

        <section className="liste-declarations-kpi-grid">

          <div className="liste-declarations-kpi-card">
            <span>Salariés CDI</span>
            <strong>{totalSalaries}</strong>
          </div>

          <div className="liste-declarations-kpi-card">
            <span>Total déclarations</span>
            <strong>{totalDeclarations}</strong>
          </div>

          <div className="liste-declarations-kpi-card">
            <span>Comptes validés</span>
            <strong className="kpi-green">{totalComptesValides}</strong>
          </div>

          <div className="liste-declarations-kpi-card">
            <span>Sans déclaration</span>
            <strong className="kpi-orange">{totalSansDeclaration}</strong>
          </div>

        </section>

        <section className="liste-declarations-toolbar-card">

          <div className="liste-declarations-toolbar-header">
            <div>
              <Typography className="liste-declarations-toolbar-title">
                Recherche et filtres
              </Typography>

              <Typography className="liste-declarations-toolbar-description">
                Recherchez un salarié ou filtrez par statut et nombre de déclarations.
              </Typography>
            </div>

            <Button
              variant="contained"
              className="liste-declarations-refresh-btn"
              onClick={refreshData}
              disabled={loading}
            >
              {loading ? "Chargement..." : "Actualiser"}
            </Button>
          </div>

          <div className="liste-declarations-filter-grid">

            <div className="liste-declarations-filter-item liste-declarations-filter-search">
              <Typography className="liste-declarations-filter-label">
                Recherche globale
              </Typography>

              <TextField
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nom, prénom, email..."
                size="small"
                fullWidth
              />
            </div>

            <div className="liste-declarations-filter-item">
              <Typography className="liste-declarations-filter-label">
                Statut du compte
              </Typography>

              <TextField
                select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="Tous">Tous les statuts</MenuItem>
                <MenuItem value="valides">Comptes validés</MenuItem>
                <MenuItem value="non_valides">À compléter</MenuItem>
              </TextField>
            </div>

            <div className="liste-declarations-filter-item">
              <Typography className="liste-declarations-filter-label">
                Déclarations
              </Typography>

              <TextField
                select
                value={filterDeclaration}
                onChange={(e) => setFilterDeclaration(e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="Tous">Tous</MenuItem>
                <MenuItem value="avec">Avec déclarations</MenuItem>
                <MenuItem value="sans">Sans déclaration</MenuItem>
              </TextField>
            </div>

            <div className="liste-declarations-filter-actions">
              <Button
                variant="outlined"
                className="liste-declarations-reset-btn"
                onClick={resetFilters}
              >
                Réinitialiser
              </Button>
            </div>

          </div>

          <div className="liste-declarations-result-line">
            {filteredSalaries.length} salarié(s) affiché(s) sur {salaries.length}
          </div>

        </section>

        <section className="liste-declarations-table-card">

          <TableContainer className="liste-declarations-table" sx={{ overflowX: "auto" }}>
            <Table>

              <TableHead>
                <TableRow>
                  <TableCell>Salarié</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Déclarations</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>

                {loading && salaries.length === 0 && (
                  <>
                    {[1, 2, 3].map((item) => (
                      <TableRow key={item}>
                        <TableCell colSpan={5}>
                          <div className="liste-declarations-skeleton-line"></div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}

                {!loading && filteredSalaries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <div className="liste-declarations-empty-state">
                        Aucun salarié trouvé.
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {filteredSalaries.map((salarie) => {
                  const valid = isCompteValide(salarie);
                  const count = Number(salarie.declarationsCount || 0);

                  return (
                    <TableRow key={salarie.id}>

                      <TableCell>
                        <div className="liste-declarations-user-cell">
                          <div className="liste-declarations-avatar">
                            {(salarie.prenom?.[0] || salarie.nom?.[0] || "?").toUpperCase()}
                          </div>

                          <div>
                            <strong>{salarie.nom} {salarie.prenom}</strong>
                            <span>ID : {salarie.id}</span>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        <span className="liste-declarations-email">
                          {salarie.email}
                        </span>
                      </TableCell>

                      <TableCell>
                        <span className={count > 0 ? "declaration-count-badge count-ok" : "declaration-count-badge count-empty"}>
                          {count} déclaration{count > 1 ? "s" : ""}
                        </span>
                      </TableCell>

                      <TableCell>
                        <span className={valid ? "status-badge status-valid" : "status-badge status-pending"}>
                          {valid ? "Compte validé" : salarie.compte_status || "À compléter"}
                        </span>
                      </TableCell>

                      <TableCell align="right">
                        <Button
                          variant="contained"
                          className="liste-declarations-action-btn"
                          onClick={() => handleNavigateToDeclarations(salarie)}
                        >
                          Voir déclarations
                        </Button>
                      </TableCell>

                    </TableRow>
                  );
                })}

              </TableBody>

            </Table>
          </TableContainer>

        </section>

      </div>

      <Snackbar
        open={notification.open}
        autoHideDuration={2500}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={notification.severity}
          sx={{ fontWeight: 'bold' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

    </LayoutDashboard>
  );

};

export default ListeDeclarations;